import { CHATGPT_IMAGE_MODELS } from './chatgptImageModels';
import { canEvaluateQwen21, QWEN21_MODEL_ID, QWEN21_DIMENSIONS } from './qwenImageModels';
// Config updated: 2026-02-09 - Added credit costs for billing system
export type ModelCapability = 'txt2img' | 'img2img' | 'inpaint' | 'edit';
export const DEFAULT_IMAGE_MODEL = 'openai/chatgpt-image' as const;
export const DEFAULT_IMAGE_MODEL_NAME = 'ChatGPT · GPT Image 2';

export type SubscriptionTier = 'free' | 'starter' | 'pro';

export interface CreditCost {
    default: number;
    [resolution: string]: number; // e.g., '1K': 15, '2K': 15, '4K': 30
}

export interface ModelConfig {
    id: string; // Provider model ID
    name: string; // Display name
    description?: string;
    icon?: string; // Path to model icon image
    provider: 'replicate' | 'pollinations' | 'google' | 'chatgpt' | 'qwen';
    evaluationOnly?: boolean;
    capabilities: ModelCapability[];
    pricing: { pro: boolean };
    creditCost: number | CreditCost; // Credits per generation (number for flat, CreditCost for resolution-dependent)
    minimumTier?: SubscriptionTier; // Minimum subscription tier required (undefined = available to all)
    supportsNegativePrompt?: boolean; // Default true. Z-Image-Turbo doesn't support negative prompts
    hidden?: boolean; // If true, don't show in model dropdown (used internally)
    inputs: {
        // Schema mapping (map 'prompt' to 'text_prompt' if needed)
        inputKeyMap?: Record<string, string>;
        maxResolution?: number;
        supportedAspectRatios?: string[];
    };
    optimization?: {
        prefix?: string;
        suffix?: string;
        negativePrompt?: string;
    };
}

export const AVAILABLE_MODELS: ModelConfig[] = [
    {
        id: QWEN21_MODEL_ID, name: 'Qwen Image 2.1 · Preview', provider: 'qwen',
        description: 'Local image generation for account-scoped evaluation',
        capabilities: ['txt2img'], pricing: { pro: false }, creditCost: 0,
        supportsNegativePrompt: false, evaluationOnly: true,
        inputs: { supportedAspectRatios: Object.keys(QWEN21_DIMENSIONS) },
    },
    ...CHATGPT_IMAGE_MODELS.map((model): ModelConfig => ({
        id: model.id === 'gpt-image-2' ? DEFAULT_IMAGE_MODEL : `openai/${model.id}`,
        name: model.id === 'gpt-image-2' ? DEFAULT_IMAGE_MODEL_NAME : `ChatGPT · ${model.name}`,
        description: 'Requested image model through the studio ChatGPT subscription; access may vary',
        icon: '/icons/openai-chatgpt.png', provider: 'chatgpt',
        capabilities: ['txt2img','img2img','edit'], pricing: {pro:false}, creditCost:1,
        supportsNegativePrompt:false,
        inputs: {supportedAspectRatios:['1:1','3:2','2:3','4:3','3:4','16:9','9:16','21:9','9:21','4:5','5:4']},
    })),

    // --- Z-Image Foundation Model (full quality, CFG, negative prompts) ---
    // Local only for now - Replicate not yet released
    // Uses BF16 model + FP8 mixed text encoder, 50 steps, CFG 4.0
    {
        id: 'tongyi-mai/z-image',
        name: 'Z-Image',
        description: 'Foundation model - 50 steps, full CFG, negative prompts',
        icon: '/assets/models/z-image.png',
        provider: 'replicate', // Will use local ComfyUI when Replicate unavailable
        capabilities: ['txt2img', 'img2img'],
        pricing: { pro: false },
        creditCost: 2, // $0.016/run
        supportsNegativePrompt: true,
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            }
        },
        optimization: {
            prefix: 'cinematic, photo, high quality, ',
            negativePrompt: 'low quality, blurry, distorted, artifacts, watermark, text, signature, deformed'
        }
    },

    // --- Z-Image Turbo (fast, 8 steps, RL-optimized) ---
    {
        id: 'prunaai/z-image-turbo',
        name: 'Z-Image Turbo',
        description: 'Fast generation - 8 steps, optimized for speed',
        icon: '/assets/models/z-image.png',
        provider: 'replicate',
        capabilities: ['txt2img', 'img2img'],
        pricing: { pro: false },
        creditCost: { default: 1, '0.5MP': 1, '1MP': 1, '2MP': 2 }, // $0.0025-$0.01/run depending on resolution
        supportsNegativePrompt: false,
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            }
        },
        optimization: {
            prefix: 'cinematic, photo, high quality, ',
        }
    },

    // --- Z-Image Turbo variants (hidden from UI, used internally) ---
    {
        id: 'prunaai/z-image-turbo-img2img',
        name: 'Z-Image Turbo (Img2Img)',
        provider: 'replicate',
        capabilities: ['img2img'],
        pricing: { pro: false },
        creditCost: 1, // Same as parent turbo model
        supportsNegativePrompt: false,
        hidden: true, // Internal use only
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            }
        },
        optimization: {
            prefix: 'cinematic, photo, high quality, ',
        }
    },
    {
        id: 'prunaai/z-image-turbo-controlnet',
        name: 'Z-Image Turbo ControlNet',
        description: 'Sketch-to-image with ControlNet guidance',
        provider: 'replicate',
        capabilities: ['img2img'],
        pricing: { pro: false },
        creditCost: 1,
        supportsNegativePrompt: false,
        hidden: true, // Internal use only
        inputs: {
            inputKeyMap: {
                'inputImage': 'control_image'
            }
        },
        optimization: {
            prefix: 'masterpiece, best quality, anime style, ',
        }
    },
    {
        id: 'prunaai/z-image-turbo-lora',
        name: 'Z-Image Turbo LoRA',
        description: 'Z-Image Turbo with LoRA support',
        provider: 'replicate',
        capabilities: ['txt2img', 'img2img'],
        pricing: { pro: false },
        creditCost: 1,
        supportsNegativePrompt: false,
        hidden: true, // Internal use only
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            }
        },
        optimization: {
            prefix: 'cinematic, photo, high quality, ',
        }
    },

    // --- Animagine XL (Anime) ---
    // Replicate: 3.1, Local ComfyUI: 4.0. Using v4.0 prompt guidelines (quality at end)
    {
        id: 'cagliostrolab/animagine-xl-3.1',
        name: 'Animagine',
        description: 'Anime illustration model - quality tags go at end of prompt',
        provider: 'replicate',
        icon: '/assets/models/animagine.png',
        capabilities: ['txt2img'],
        pricing: { pro: false },
        creditCost: 1, // $0.004/run — cheapest model
        supportsNegativePrompt: true,
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            },
            supportedAspectRatios: ['1:1', '9:7', '3:2', '7:4', '12:5', '7:9', '2:3', '4:7', '5:12']
        },
        optimization: {
            // v4.0: Quality tags go at END of prompt (after rating tag)
            suffix: ', safe, masterpiece, high score, great score, absurdres',
            negativePrompt: 'lowres, bad anatomy, bad hands, text, error, missing finger, extra digits, fewer digits, cropped, worst quality, low quality, low score, bad score, average score, signature, watermark, username, blurry'
        }
    },

    // --- Google (Direct API) ---
    // Nano Banana = gemini-2.5-flash-image (fast, efficient)
    // Nano Banana Pro = gemini-3-pro-image-preview (professional quality)
    {
        id: 'google/nano-banana',
        hidden: true,
        name: 'Nano Banana',
        provider: 'google',
        capabilities: ['txt2img', 'img2img'],
        pricing: { pro: false },
        creditCost: 5, // $0.039/run
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            },
            supportedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '21:9', '9:21', '3:2', '2:3', '4:5', '5:4']
        },
        optimization: {
            prefix: 'high quality, detailed, professional, ',
            negativePrompt: 'low quality, blurry, distorted, artifact, watermark, text, signature'
        }
    },
    {
        id: 'google/nano-banana-pro',
        name: 'Nano Banana Pro',
        provider: 'google',
        capabilities: ['txt2img', 'img2img'],
        pricing: { pro: false },
        creditCost: { default: 15, '1K': 15, '2K': 15, '4K': 30 }, // $0.15-$0.30/run
        minimumTier: 'starter', // Gated from free users in Lookbook
        hidden: true, // Removed from the generator; retained for existing internal callers.
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            },
            supportedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '21:9', '9:21', '3:2', '2:3', '4:5', '5:4']
        },
        optimization: {
            prefix: 'high quality, detailed, professional, ',
            negativePrompt: 'low quality, blurry, distorted, artifact, watermark, text, signature'
        }
    },
    {
        id: 'models/gemini-3-pro-image-preview',
        name: 'Gemini 3 Pro (Preview)',
        provider: 'google',
        capabilities: ['txt2img', 'img2img'],
        pricing: { pro: false },
        creditCost: 15, // Same as Nano Banana Pro — used internally for reference sheets
        hidden: true, // Only used for reference sheets internally
        inputs: {
            inputKeyMap: {
                'inputImage': 'image'
            },
            supportedAspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '21:9', '9:21', '3:2', '2:3', '4:5', '5:4']
        },
        optimization: {
            prefix: 'high quality, detailed, professional, ',
            negativePrompt: 'low quality, blurry, distorted, artifact, watermark, text, signature'
        }
    }
];

export function getModelConfig(modelId: string): ModelConfig | undefined {
    return AVAILABLE_MODELS.find(m => m.id === modelId);
}

// Get only visible models for UI dropdowns
export function getVisibleModels(evaluationUserId?: string): ModelConfig[] {
    return AVAILABLE_MODELS.filter(m => !m.hidden && (!m.evaluationOnly || canEvaluateQwen21(evaluationUserId)));
}
